/**
 * 内容解析器
 * 支持 Markdown 格式和自定义交互链接
 *
 * 支持的格式：
 * - 路由链接：[文本](route:/path/to/page?param=value)
 * - 自定义操作：[文本](action:actionName)
 * - 颜色文本：[文本](color:#ff0000) 或 [文本](color:red)
 * - 加粗文本：**文本**
 * - 换行：\n
 */
export class ContentParser {
  /**
   * 内容格式枚举
   */
  static ContentFormat = {
    PLAIN: 0,
    MARKDOWN: 1,
    HTML: 2,
    JSON: 3
  }

  /**
   * 解析内容为安全的 HTML
   * @param {string} content - 内容
   * @param {number} contentFormat - 内容格式（0=纯文本, 1=Markdown, 2=HTML, 3=JSON）
   * @returns {string} 安全的 HTML 字符串
   */
  static parseToHtml(content, contentFormat = this.ContentFormat.MARKDOWN) {
    if (!content) return ''

    // 根据格式选择解析方式
    switch (contentFormat) {
      case this.ContentFormat.PLAIN:
        return this.parsePlainText(content)
      case this.ContentFormat.MARKDOWN:
        return this.parseMarkdown(content)
      case this.ContentFormat.HTML:
        return this.parseHtml(content)
      case this.ContentFormat.JSON:
        return this.parseJson(content)
      default:
        // 兼容旧数据：如果没有格式字段，尝试自动检测
        return this.parseMarkdown(content)
    }
  }

  /**
   * 解析纯文本
   */
  static parsePlainText(content) {
    return this.escapeHtml(content).replace(/\n/g, '<br>')
  }

  /**
   * 解析 Markdown 格式（原有逻辑）
   */
  static parseMarkdown(content) {
    if (!content) return ''

    let html = content

    // 先处理特殊格式，使用临时占位符避免被转义
    const placeholders = []
    let placeholderIndex = 0

    // 处理路由链接：[文本](route:/path/to/page?param=value)
    html = html.replace(/\[([^\]]+)\]\((route):([^)]+)\)/g, (match, linkText, linkType, linkTarget) => {
      const placeholder = `__LINK_ROUTE_${placeholderIndex}__`
      placeholders.push({
        placeholder,
        replacement: `<a href="#" class="message-link" data-link-type="route" data-link-target="${this.escapeHtml(encodeURIComponent(linkTarget))}">${this.escapeHtml(linkText)}</a>`
      })
      placeholderIndex++
      return placeholder
    })

    // 处理自定义操作链接：[文本](action:actionName)
    html = html.replace(/\[([^\]]+)\]\((action):([^)]+)\)/g, (match, linkText, linkType, actionName) => {
      const placeholder = `__LINK_ACTION_${placeholderIndex}__`
      placeholders.push({
        placeholder,
        replacement: `<a href="#" class="message-link message-action" data-link-type="action" data-action="${this.escapeHtml(actionName)}">${this.escapeHtml(linkText)}</a>`
      })
      placeholderIndex++
      return placeholder
    })

    // 处理颜色文本：[文本](color:#ff0000) 或 [文本](color:red)
    html = html.replace(/\[([^\]]+)\]\((color):([^)]+)\)/g, (match, text, linkType, color) => {
      const placeholder = `__COLOR_${placeholderIndex}__`
      const safeColor = this.validateColor(color.trim())
      placeholders.push({
        placeholder,
        replacement: `<span style="color: ${safeColor}">${this.escapeHtml(text)}</span>`
      })
      placeholderIndex++
      return placeholder
    })

    // 处理加粗：**文本**
    html = html.replace(/\*\*([^*]+)\*\*/g, (match, text) => {
      const placeholder = `__BOLD_${placeholderIndex}__`
      placeholders.push({
        placeholder,
        replacement: `<strong>${this.escapeHtml(text)}</strong>`
      })
      placeholderIndex++
      return placeholder
    })

    // 转义 HTML 特殊字符
    html = html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

    // 恢复占位符
    placeholders.forEach(({ placeholder, replacement }) => {
      html = html.replace(placeholder, replacement)
    })

    // 处理换行（在转义之后处理，避免被转义）
    html = html.replace(/\n/g, '<br>')

    return html
  }

  /**
   * 解析 HTML 格式
   */
  static parseHtml(content) {
    // HTML 格式需要严格过滤，防止 XSS
    // 这里可以使用 DOMPurify 等库进行安全过滤
    // 暂时简单处理：直接返回（实际项目中应该使用专业的 HTML 清理库）
    // TODO: 集成 DOMPurify 等安全库进行 HTML 过滤
    return content
  }

  /**
   * 解析 JSON 结构化格式
   */
  static parseJson(content) {
    try {
      const data = JSON.parse(content)
      // 根据 JSON 结构渲染
      // 这里可以根据实际需求实现
      return this.parseMarkdown(data.text || content)
    } catch (e) {
      console.error('JSON 格式解析失败:', e)
      return this.escapeHtml(content)
    }
  }

  /**
   * HTML 转义
   * @param {string} text - 要转义的文本
   * @returns {string} 转义后的文本
   */
  static escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  /**
   * 验证和规范化颜色值
   * 支持 hex (#ff0000, #f00)、rgb(rgb(255,0,0))、rgba 和 CSS 颜色名称
   * @param {string} color - 颜色值
   * @returns {string} 安全的颜色值
   */
  static validateColor(color) {
    if (!color) return 'inherit'

    const trimmedColor = color.trim()

    // CSS 颜色名称白名单（常用颜色）
    const cssColorNames = [
      'black',
      'silver',
      'gray',
      'white',
      'maroon',
      'red',
      'purple',
      'fuchsia',
      'green',
      'lime',
      'olive',
      'yellow',
      'navy',
      'blue',
      'teal',
      'aqua',
      'orange',
      'pink',
      'brown',
      'cyan',
      'magenta',
      'transparent',
      'inherit'
    ]

    // 检查是否是 CSS 颜色名称
    if (cssColorNames.includes(trimmedColor.toLowerCase())) {
      return trimmedColor.toLowerCase()
    }

    // 检查是否是 hex 颜色 (#fff, #ffffff)
    if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(trimmedColor)) {
      return trimmedColor
    }

    // 检查是否是 rgb/rgba 颜色
    if (/^rgba?\([\d\s,.]+\)$/.test(trimmedColor)) {
      return trimmedColor
    }

    // 如果不匹配任何有效格式，返回默认值
    return 'inherit'
  }

  /**
   * 处理链接点击事件
   * @param {HTMLElement} element - 链接元素
   * @param {Function} onAction - 自定义操作回调
   * @param {Function} onRoute - 路由跳转回调
   */
  static handleLinkClick(element, { onAction = () => {}, onRoute = () => {} }) {
    const linkType = element.getAttribute('data-link-type')
    const linkTarget = element.getAttribute('data-link-target')
    const action = element.getAttribute('data-action')

    if (linkType === 'route' && linkTarget) {
      try {
        const target = decodeURIComponent(linkTarget)
        const [path, queryString] = target.split('?')
        const query = {}

        if (queryString) {
          queryString.split('&').forEach((param) => {
            const [key, value] = param.split('=')
            if (key && value) {
              query[decodeURIComponent(key)] = decodeURIComponent(value)
            }
          })
        }

        onRoute({ path, query })
      } catch (error) {
        console.error('路由跳转失败:', error)
      }
    } else if (linkType === 'action' && action && onAction) {
      onAction(action)
    }
  }
}

export default ContentParser
